import QRCode from 'qrcode'
import { useEffect, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import type { LoginResponse } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { Wordmark } from '@/ui/Masthead'

type Step = 'email' | 'password' | 'totp_setup' | 'totp_verify'

export function LoginPage() {
  const { token, login, completeTotpSetup, verifyTotp } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [challenge, setChallenge] = useState<LoginResponse | null>(null)
  const [qrSrc, setQrSrc] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [step, setStep] = useState<Step>('email')
  const [bootLine, setBootLine] = useState(0)

  useEffect(() => {
    if (bootLine >= BOOT_LINES.length) return
    const t = setTimeout(() => setBootLine((n) => n + 1), 110)
    return () => clearTimeout(t)
  }, [bootLine])

  useEffect(() => {
    let cancelled = false
    async function buildQr() {
      if (challenge?.status !== 'totp_setup_required') {
        setQrSrc(null)
        return
      }
      const src = await QRCode.toDataURL(challenge.otpauth_uri, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 220,
      })
      if (!cancelled) setQrSrc(src)
    }
    void buildQr()
    return () => {
      cancelled = true
    }
  }, [challenge])

  if (token) return <Navigate to="/" replace />

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (step === 'email') {
      if (!email.trim()) return
      setStep('password')
      return
    }

    setBusy(true)
    try {
      if (step === 'password') {
        const resp = await login(email.trim(), password)
        if (resp.status === 'authenticated') {
          navigate('/', { replace: true })
          return
        }
        setChallenge(resp)
        setPassword('')
        setTotpCode('')
        setStep(resp.status === 'totp_setup_required' ? 'totp_setup' : 'totp_verify')
        return
      }

      if (!challenge || challenge.status === 'authenticated') {
        throw new Error('Login challenge expired')
      }

      if (step === 'totp_setup') {
        await completeTotpSetup(challenge.challenge_token, totpCode)
      } else {
        await verifyTotp(challenge.challenge_token, totpCode)
      }
      navigate('/', { replace: true })
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : 'Login failed'
      setError(friendlyLoginError(raw))
      setTotpCode('')
      if (step === 'password') {
        setPassword('')
      }
    } finally {
      setBusy(false)
    }
  }

  function goBack() {
    setError(null)
    setTotpCode('')
    if (step === 'password') {
      setPassword('')
      setStep('email')
      return
    }
    setChallenge(null)
    setQrSrc(null)
    setStep('password')
  }

  const canSubmit =
    step === 'email'
      ? !!email.trim()
      : step === 'password'
        ? !!password
        : totpCode.trim().length >= 6

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl panel">
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-line bg-surface">
          <div className="flex items-center gap-2 font-mono text-[12px] text-fg-mute">
            <span className="inline-block w-2 h-2 bg-danger" />
            <span className="inline-block w-2 h-2 bg-warn" />
            <span className="inline-block w-2 h-2 bg-accent" />
            <span className="ml-2">remote-ai · login</span>
          </div>
          <span className="font-mono text-[11.5px] text-fg-dim">tty0</span>
        </div>

        <div className="px-4 py-4 font-mono text-[14.5px] text-fg leading-[1.55]">
          <div className="space-y-0.5 text-fg-dim">
            {BOOT_LINES.slice(0, bootLine).map((line, i) => (
              <div key={i} className="animate-fade-in">
                {line.ok && <span className="text-accent">[ ok ]</span>}
                {!line.ok && <span className="text-fg-dim">[ .. ]</span>}{' '}
                <span className="text-fg-soft">{line.text}</span>
              </div>
            ))}
          </div>

          {bootLine >= BOOT_LINES.length && (
            <div className="mt-4 animate-fade-in">
              <div className="text-fg-soft">
                <span className="text-accent">remote-ai</span> v0.1.0 — ai operator console.
              </div>
              <div className="text-fg-dim mt-0.5">
                sign in to continue. use your account credentials.
              </div>

              <form onSubmit={onSubmit} className="mt-5 space-y-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-accent shrink-0">login:</span>
                  {step === 'email' ? (
                    <input
                      autoFocus
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1 bg-transparent border-0 outline-none font-mono text-[14.5px] text-fg placeholder:text-fg-dim"
                      placeholder="you@example.com"
                    />
                  ) : (
                    <span className="text-fg">{email}</span>
                  )}
                  {step === 'email' && <span className="caret" />}
                </div>

                {step === 'password' && (
                  <div className="flex items-baseline gap-2 animate-fade-in">
                    <span className="text-accent shrink-0">password:</span>
                    <input
                      autoFocus
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="flex-1 bg-transparent border-0 outline-none font-mono text-[14.5px] text-fg placeholder:text-fg-dim"
                      placeholder="••••••••"
                    />
                    <span className="caret" />
                  </div>
                )}

                {step === 'totp_verify' && (
                  <div className="space-y-2 animate-fade-in">
                    <div className="text-fg-soft">
                      enter the 6-digit code from your authenticator app.
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-accent shrink-0">totp:</span>
                      <input
                        autoFocus
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                        className="flex-1 bg-transparent border-0 outline-none font-mono tracking-[0.25em] text-[14.5px] text-fg placeholder:text-fg-dim"
                        placeholder="123456"
                      />
                      <span className="caret" />
                    </div>
                  </div>
                )}

                {step === 'totp_setup' && challenge?.status === 'totp_setup_required' && (
                  <div className="space-y-3 animate-fade-in">
                    <div className="text-fg-soft">
                      scan the qr code with your authenticator app, then enter the first code to finish setup.
                    </div>
                    <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)] items-start">
                      <div className="border border-line bg-white p-3 min-h-[220px] flex items-center justify-center">
                        {qrSrc ? (
                          <img src={qrSrc} alt="Authenticator setup QR code" className="w-full h-auto" />
                        ) : (
                          <span className="text-[12px] text-fg-dim">rendering qr…</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="text-fg-dim text-[12px] uppercase tracking-[0.08em]">
                          manual setup
                        </div>
                        <div className="text-fg-soft break-all">
                          issuer: {challenge.issuer}
                        </div>
                        <div className="text-fg-soft break-all">
                          account: {challenge.account_name}
                        </div>
                        <div className="text-fg break-all">
                          secret: <span className="text-accent">{challenge.manual_secret}</span>
                        </div>
                        <div className="text-fg-dim text-[12px]">
                          if qr scan fails, enter that secret manually in your authenticator app.
                        </div>
                      </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-accent shrink-0">verify:</span>
                      <input
                        autoFocus
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        required
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                        className="flex-1 bg-transparent border-0 outline-none font-mono tracking-[0.25em] text-[14.5px] text-fg placeholder:text-fg-dim"
                        placeholder="123456"
                      />
                      <span className="caret" />
                    </div>
                  </div>
                )}

                {error && (
                  <div className="pt-2 animate-fade-in">
                    <span className="text-danger">error:</span>{' '}
                    <span className="text-fg">{error}</span>
                  </div>
                )}

                <div className="pt-3 flex items-center gap-3">
                  {step !== 'email' && (
                    <button
                      type="button"
                      onClick={goBack}
                      className="btn btn-ghost"
                      disabled={busy}
                    >
                      <span className="text-fg-dim">[</span>back<span className="text-fg-dim">]</span>
                    </button>
                  )}
                  <button type="submit" disabled={busy || !canSubmit} className="btn btn-primary">
                    <span className="text-fg-dim">[</span>
                    {busy
                      ? 'working…'
                      : step === 'email'
                        ? 'next'
                        : step === 'password'
                          ? 'sign in'
                          : step === 'totp_setup'
                            ? 'enable 2fa'
                            : 'verify 2fa'}
                    <span className="text-[11px] text-fg-dim ml-1">↵</span>
                    <span className="text-fg-dim">]</span>
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="border-t border-line bg-surface px-3 py-1.5 flex items-center justify-between font-mono text-[11.5px] text-fg-dim">
          <span className="flex items-center gap-1.5">
            <Wordmark size="sm" />
          </span>
          <span>{statusLabel(step)}</span>
          <span>
            {new Date().toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      </div>
    </div>
  )
}

const BOOT_LINES: { text: string; ok: boolean }[] = [
  { text: 'mounting /etc/remote-ai', ok: true },
  { text: 'loading session store', ok: true },
  { text: 'connecting to api', ok: true },
  { text: 'authenticating terminal', ok: true },
]

function statusLabel(step: Step): string {
  switch (step) {
    case 'email':
      return 'INSERT · login'
    case 'password':
      return 'INSERT · password'
    case 'totp_setup':
      return 'INSERT · 2fa setup'
    case 'totp_verify':
      return 'INSERT · 2fa verify'
  }
}

function friendlyLoginError(raw: string): string {
  const lower = raw.toLowerCase()
  if (lower.includes('invalid') && lower.includes('totp')) return 'invalid 2fa code'
  if (lower.includes('setup expired') || lower.includes('challenge expired')) {
    return 'login session expired'
  }
  if (lower.includes('invalid challenge')) return 'login session expired'
  if (lower.includes('too many totp')) return 'too many 2fa attempts'
  if (lower.includes('too many login')) return 'too many login attempts'
  if (lower.includes('invalid') || lower.includes('incorrect') || lower.includes('credential')) {
    return 'invalid credentials'
  }
  if (lower.includes('disabled')) return 'account disabled — contact an admin'
  if (lower.includes('network') || lower.includes('failed to fetch')) return 'cannot reach server'
  return raw
}
